/**
 * AI-DART 화면 공용 컨트롤.
 *
 * `shared/ui/atoms.tsx`의 `Chip`은 표시용 `<span>`이라 토글이 안 되고, `StatusDot`은 보드 상태
 * 어휘(todo·progress·done)에 묶여 있다. 둘 다 AI-DART의 필터·활성 표현에 맞지 않아 여기 둔다 —
 * shared를 AI-DART 어휘로 넓히면 보드 화면이 그 어휘를 떠안는다.
 */

/** 필터 토글 칩. 누르면 켜지고 다시 누르면 꺼진다(해제). */
export function FilterChip({
  active, onClick, title, children,
}: {
  active?: boolean;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      title={title}
      aria-pressed={active}
      onClick={onClick}
      className={`cursor-pointer rounded-full border px-2.5 py-1 text-[11px] whitespace-nowrap transition ${
        active
          ? 'border-accent bg-accent font-semibold text-white'
          : 'border-line bg-surface text-muted hover:border-accent-line hover:bg-line-soft hover:text-accent'
      }`}
    >
      {children}
    </button>
  );
}

/** 조작 버튼 — 링크처럼 보이던 텍스트 버튼을 실제 버튼 모양으로. */
export function ActionButton({
  tone = 'default', onClick, disabled, title, children,
}: {
  tone?: 'default' | 'primary' | 'danger';
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const skin =
    tone === 'primary'
      ? 'border-accent bg-accent text-white hover:brightness-95 active:brightness-90'
      : tone === 'danger'
        ? 'border-rose-200 bg-white text-rose-600 hover:border-rose-300 hover:bg-rose-50 active:bg-rose-100'
        : 'border-line bg-surface text-muted hover:border-accent-line hover:bg-line-soft hover:text-accent active:bg-line';
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      /**
       * `cursor-pointer`를 명시하는 이유: Tailwind v4의 preflight가 버튼의 기본 `cursor: pointer`를
       * 없앴다(v3까지는 브라우저 기본이 그대로 살아 있었다). 지정하지 않으면 화살표 커서라 눌리는
       * 물건으로 보이지 않는다 — 이 저장소 전체가 지금 그 상태다(`cursor-pointer` 사용처 0건, 실측).
       */
      className={`cursor-pointer rounded-md border px-2.5 py-1 text-[11px] whitespace-nowrap transition disabled:cursor-not-allowed disabled:opacity-40 ${skin}`}
    >
      {children}
    </button>
  );
}

/** 활성 여부 점 — 모델·인커밍 어댑터가 같은 표현을 쓴다. */
export function ActiveDot({ on, label }: { on: boolean; label?: string }): React.JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className={`inline-block size-2 rounded-full ${on ? 'bg-emerald-500' : 'bg-slate-300'}`} />
      {label && <span className={on ? 'text-emerald-700' : 'text-muted'}>{label}</span>}
    </span>
  );
}

/** 설정 표 공통 스킨 — 네 화면이 같은 테두리·헤더를 쓰게 한다. */
export const TABLE = 'w-full border-collapse text-left text-[12px]';
export const THEAD = 'border-b border-line bg-line-soft text-[11px] text-muted';
export const TH = 'px-3 py-2 font-medium';
export const TD = 'px-3 py-2.5 align-middle';
export const TR = 'border-b border-line last:border-0';
